import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import TopNavbar from './components/TopNavbar';
import LeftSidebar from './components/LeftSidebar';
import LoginModal from './components/LoginModal';
import RegistrationPage from './pages/RegistrationPage';
import AlertsPage from './pages/AlertsPage';
import AssetsPage from './pages/AssetsPage';
import NetworksPage from './pages/NetworksPage';
import SensorsPage from './pages/SensorsPage';
import NotificationsPage from './pages/NotificationsPage';
import QuestionsPage from './pages/QuestionsPage';
import LogsPage from './pages/LogsPage';
import DocsPage from './pages/DocsPage'
import ProfilesPage from './pages/ProfilesPage';

import './App.css';

const App = () => {
  useEffect(() => {
    document.title = 'Network Security Console';
  }, []);

  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
    </AuthProvider>
  );
};

const AppRoutes = () => {
  const { isAuthenticated } = useAuth();

  return (
    <Routes>
      {/* Root path - redirect based on auth status */}
      <Route
        path="/"
        element={isAuthenticated() ? <Navigate to="/alerts" replace /> : <UnauthenticatedLayout />}
      />

      {/* Protected Routes */}
      <Route element={<ProtectedRouteGuard />}>
        <Route path="/alerts" element={<ProtectedLayout><AlertsPage /></ProtectedLayout>} />
        <Route path="/assets" element={<ProtectedLayout><AssetsPage /></ProtectedLayout>} />
        <Route path="/networks" element={<ProtectedLayout><NetworksPage /></ProtectedLayout>} />
        <Route path="/sensors" element={<ProtectedLayout><SensorsPage /></ProtectedLayout>} />
        <Route path="/notifications" element={<ProtectedLayout><NotificationsPage /></ProtectedLayout>} />
        <Route path="/questions" element={<ProtectedLayout><QuestionsPage /></ProtectedLayout>} />
        <Route path="/logs" element={<ProtectedLayout><LogsPage /></ProtectedLayout>} />
        <Route path="/docs" element={<ProtectedLayout><DocsPage /></ProtectedLayout>} />
        <Route path="/profiles" element={<ProtectedLayout><ProfilesPage /></ProtectedLayout>} />
      </Route>

      {/* Catch all - redirect to root */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

// Route guard for all protected routes
const ProtectedRouteGuard = () => {
  const { isAuthenticated } = useAuth();

  if (!isAuthenticated()) {
    return <Navigate to="/" replace />;
  }

  return <Outlet />;
};

const UnauthenticatedLayout = () => {
  const [loginModalOpen, setLoginModalOpen] = useState(false);

  return (
    <div>
      <TopNavbar setLoginModalOpen={setLoginModalOpen} />
      {loginModalOpen && <LoginModal closeModal={() => setLoginModalOpen(false)} />}
      <RegistrationPage />
    </div>
  );
};

const ProtectedLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const [notificationsRefreshKey, setNotificationsRefreshKey] = useState(0);
  const refreshNotifications = () => {
    setNotificationsRefreshKey(prev => prev + 1);
  };

  return (
    <div className={`App ${sidebarOpen ? '' : 'no-sidebar'}`}>
      {/* Top Navbar */}
      <div className="top-navbar">
        <TopNavbar toggleSidebar={toggleSidebar} />
      </div>

      {/* Left Sidebar */}
      {sidebarOpen && (
        <div className="left-sidebar">
          <LeftSidebar notificationsRefreshKey={ notificationsRefreshKey }/>
        </div>
      )}

      {/* Main Content */}
      <div className={`main-content ${sidebarOpen ? '' : 'full-width'}`}>
        {React.cloneElement(children, { refreshNotifications })}
      </div>
    </div>
  );
};

export default App;
